import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react'
import {
  Badge,
  Breadcrumb,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  InputGroup,
  ListGroup,
  Row,
  Spinner,
} from 'react-bootstrap'
import { useDisclourse } from '../../hooks/useDisclourse'
import { ModalAddUser } from './components/ModalAddUser'
// import Select from 'react-select'
import { Link, useNavigate } from 'react-router-dom'
import { OffCanvaAsideFilters } from './components/OffCanvaAsideFilters'
import { ModalAddManage } from './components/ModalAddManage'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { IResAllUsers } from '../../types/users'
import { toast } from 'react-toastify'
import { getColorRamdomBoots, parseId } from '../../helpers'
import { PaginationSection } from '../shared/PaginationSection'
import { cleanVariables, PaginationUtils } from '../shared/PaginationUtils'
import { SIZE_PAGE_DEFAULT } from '../../helpers/consts'
import { IResGroups } from '../../types/assignmentGroups'
import { GET_ALL_GROUPS } from '../../gql/queries/assignmentGroupQuery'
import { GET_ALL_USERS_INFO } from '../../gql/queries/usersQuery'
import { GET_ROLES_GROUP } from '../../gql/queries/rolesGroupQuery'
import { IResRolesGroups } from '../../types/roles'
import { UserAvatar } from '../../components/UserAvatar'
import { useGetUser } from '../../store/userStore'
import { GET_ALL_ORGANIZATIONS } from '../../gql/queries/organizationsQuery'
import { REQUEST_PASSWORD_RESET } from '../../gql/mutations/authMutations'
import { PhoneProviderActions } from '../../components/telephony/PhoneProviderActions'

// Hook para manejar localStorage
export function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? (JSON.parse(item) as T) : initialValue
    } catch {
      return initialValue
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue))
    } catch {
      // puede fallar en modo incógnito
    }
  }, [key, storedValue])

  return [storedValue, setStoredValue] as const
}

export const UsersPage = () => {
  const [gqlGetMembers, { data, loading, error, refetch }] = useLazyQuery<IResAllUsers>(
    GET_ALL_USERS_INFO,
    {
      fetchPolicy: 'no-cache',
    }
  )
  const [requestInvite] = useMutation(REQUEST_PASSWORD_RESET)
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const navigate = useNavigate()
  const { isOpen, onClose, onOpen } = useDisclourse()
  const { isOpen: isOpenOffCanva, onClose: onCloseOffCanva, onOpen: onOpenOffCanva } = useDisclourse()
  const { isOpen: isOpenManage, onClose: onCloseManage, onOpen: onOpenManage } = useDisclourse()
  const [dataTable, setDataTable] = useState<IResAllUsers['allUsers']['edges']>([])

  const [arrShowMoreTable, setArrShowMoreTable] = useState<{ id: string | number; show: boolean }[]>(
    []
  )
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)
  const [totals, setTotals] = useState<{
    totalCount: number | null
    totalCountEnable: number | null
    totalCountDisable: number | null
  }>({
    totalCount: null,
    totalCountEnable: null,
    totalCountDisable: null,
  })
  const [currentPage, setCurrentPage] = useState(1)
  let totalElements = data?.allUsers?.totalCount ?? 0

  const totalPages = Math.ceil(totalElements / first)
  const { start, end } = PaginationUtils.getPaginationRange(currentPage, first, totalElements)

  // Separar filtros: los que se guardan en localStorage y los temporales
  const [savedFilters, setSavedFilters] = useLocalStorage('users-filters', {
    text: '',
    role: '',
    organizationId: '',
  })

  const [formData, setFormData] = useState({
    tabFilters: 'All Users',
    text: savedFilters.text,
    groupAssignment: '',
    isActive: null as null | boolean,
    role: savedFilters.role,
    organizationId: savedFilters.organizationId,
  })

  // Sincronizar cambios de text y role con localStorage
  useEffect(() => {
    setSavedFilters({
      text: formData.text,
      role: formData.role,
      organizationId: formData.organizationId,
    })
  }, [formData.text, formData.role, formData.organizationId])

  const updatePage = (n: number) => {
    setCurrentPage(n)
  }

  const setTabFilters: Dispatch<SetStateAction<string>> = (value) => {
    setFormData((prev) => ({
      ...prev,
      tabFilters: typeof value === 'function' ? value(prev.tabFilters) : value,
    }))
  }

  useEffect(() => {
    if (data?.allUsers) {
      // Actualizar totales cada vez que cambian los datos
      setTotals({
        totalCount: data.allUsers.totalCount,
        totalCountEnable: data.allUsers.totalCountEnable,
        totalCountDisable: data.allUsers.totalCountDisable,
      })
    }
  }, [data])

  useEffect(() => {
    handleFilters()
  }, [
    gqlGetMembers,
    currentPage,
    first,
    formData.tabFilters,
    formData.role,
    formData.organizationId,
    formData.isActive,
  ])

  const handleFilters = () => {
    const variables = cleanVariables({
      first,
      offset: (currentPage - 1) * first,
      name: formData.text,
      groupAssignment: formData.tabFilters === 'All Users' ? '' : parseId(formData.tabFilters),
      role: Number(parseId(formData.role)) > 0 ? parseId(formData.role) : null,
      isActive: formData.isActive,
      organization: isSuperAdmin && formData.organizationId ? parseId(formData.organizationId) : null,
    })

    gqlGetMembers({ variables })
  }

  const handleResendInvite = async (email: string) => {
    if (!email) {
      toast.error('User email is missing.')
      return
    }

    try {
      const response = await requestInvite({
        variables: { email, isInvitation: true },
      })
      const message =
        response.data?.requestPasswordReset?.message || 'Invitation email sent.'
      toast.success(message)
    } catch {
      toast.error('Unable to send invitation email.')
    }
  }

  useEffect(() => {
    if (!data) return
    const { edges } = data.allUsers
    setDataTable(edges)
    setArrShowMoreTable(edges.map(({ node }) => ({ id: node.id, show: false })))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  const {
    data: dataGroups,
    loading: loadingGroups,
    error: errorGroups,
    refetch: refetchGroups,
  } = useQuery<IResGroups>(GET_ALL_GROUPS, {
    fetchPolicy: 'no-cache',
    variables: {
      first: 100,
      organization: isSuperAdmin && formData.organizationId ? parseId(formData.organizationId) : null,
    },
  })

  const {
    data: dataRoles,
    loading: loadingRoles,
    error: errorRoles,
  } = useQuery<IResRolesGroups>(GET_ROLES_GROUP, { fetchPolicy: 'no-cache' })
  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: { first: 100 },
    fetchPolicy: 'no-cache',
    skip: !isSuperAdmin,
  })

  useEffect(() => {
    if (errorGroups) toast.error('Error: ' + errorGroups.message)
  }, [errorGroups])
  useEffect(() => {
    // Refetch groups when organization filter changes (only superadmin needs it)
    if (isSuperAdmin) {
      refetchGroups({
        first: 100,
        organization: formData.organizationId ? parseId(formData.organizationId) : null,
      }).catch(() => { })
    }
  }, [formData.organizationId, isSuperAdmin, refetchGroups])
  useEffect(() => {
    if (errorOrganizations) toast.error('Error: ' + errorOrganizations.message)
  }, [errorOrganizations])

  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='d-blok d-md-none' onClick={onOpenOffCanva}>
            <span>
              <i className='bi bi-list fs-1' />
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Users</Breadcrumb.Item>
          {/* <Breadcrumb.Item active>{isOpen ? 'Add user' : 'View users'} </Breadcrumb.Item> */}
        </Breadcrumb>

        <Button size='sm' onClick={onOpen}>
          <i className='bi bi-plus fs-3' /> New User
        </Button>
      </header>
      <Row className='policies'>
        <Col md={3} lg={3} sm={0} xl={3} className='menu'>
          <div className='policies-container'>
            <div>
              <div className='title-header'>
                <h3>User Status</h3>
              </div>
              <div className='user-status'>
                <div
                  onClick={() => setFormData((prev) => ({ ...prev, isActive: true }))}
                  className='status-active'
                >
                  <span className='fw-bold'>Active users</span>
                  <span className='badge badge-success'>{totals.totalCountEnable}</span>
                </div>
                <div
                  onClick={() => setFormData((prev) => ({ ...prev, isActive: false }))}
                  className='status-inactive'
                >
                  <span className='fw-bold'>Inactive users</span>
                  <span className='badge badge-danger'>{totals.totalCountDisable}</span>
                </div>
              </div>
            </div>
            <div>
              <div className='title-header'>
                <h3>User Groups</h3>
                <Link to='/assignment-groups'>
                  <i className='bi bi-pencil-square' style={{ color: 'inherit' }} /> Manage
                </Link>
              </div>
              <div className='list-menu'>
                <ListGroup variant='flush'>
                  <ListGroup.Item
                    action
                    variant={formData.tabFilters === 'All Users' ? 'primary' : 'light'}
                    className='my-2 py-4'
                    onClick={(e) =>
                      setFormData((prev) => ({
                        tabFilters: 'All Users',
                        text: '',
                        groupAssignment: '',
                        isActive: null as null | boolean,
                        role: '',
                        organizationId: '',
                      }))
                    }
                  >
                    <i className='bi bi-person-fill fs-3' style={{ color: 'inherit' }} /> All Users
                  </ListGroup.Item>
                  {!dataGroups || loadingGroups ? (
                    <div className='d-flex flex-center' style={{ height: '220px' }}>
                      <Spinner animation='border' />
                    </div>
                  ) : (
                    dataGroups?.allGroupAssignments.edges.map(({ node: lis }, idx) => (
                      <ListGroup.Item
                        key={lis.id}
                        action
                        variant={formData.tabFilters === lis.id ? 'primary' : 'light'}
                        className='my-1 py-4 d-flex align-items-center justify-content-between'
                        onClick={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            tabFilters: lis.id,
                          }))
                        }
                      >
                        <span className='d-flex align-items-center justify-content-between gap-1'>
                          <i className='bi bi-file-person-fill fs-3' style={{ color: 'inherit' }} />{' '}
                          {lis.name}
                        </span>
                        <span className={`badge badge-${getColorRamdomBoots(idx)}`}>
                          {lis.user.edges.length}
                        </span>
                      </ListGroup.Item>
                    ))
                  )}
                </ListGroup>
              </div>
            </div>
          </div>
        </Col>
        <Col md={8} lg={8} sm={12} xl={9} className='container'>
          <div className='users-container'>
            <div className='title-header'>
              <h3>List of users</h3>
            </div>
            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder='Type your query here...'
                  aria-label="Recipient's username with two button addons"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      text: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault() // evita el submit automático
                      handleFilters()
                    }
                  }}
                  value={formData.text}
                />
                <Form.Select
                  name='rol'
                  value={formData.role}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      role: e.target.value,
                    }))
                  }
                  disabled={loadingRoles}
                >
                  {loadingRoles ? (
                    <option value=''>Loading...</option>
                  ) : (
                    <option value=''>All Roles</option>
                  )}
                  {dataRoles?.allGroups.edges.map(({ node }) => (
                    <option key={node.id} value={node.id}>
                      {node.name}
                    </option>
                  ))}
                </Form.Select>
                {isSuperAdmin && (
                  <Form.Select
                    name='organizationId'
                    value={formData.organizationId}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        organizationId: e.target.value,
                      }))
                    }
                    disabled={loadingOrganizations}
                  >
                    {loadingOrganizations ? (
                      <option value=''>Loading...</option>
                    ) : (
                      <option value=''>All Organizations</option>
                    )}
                    {dataOrganizations?.allOrganizations?.edges?.map(({ node }: any) => (
                      <option key={node.id} value={parseId(node.id)}>
                        {node.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Button
                  onClick={handleFilters}
                  variant='outline-success'
                  style={{ border: '1px solid #dbdfe9' }}
                >
                  Search
                </Button>
              </InputGroup>
            </div>
            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {
                  setFirst(n)
                  setCurrentPage(1)
                  // setOffset(0)
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalElements}
                updatePage={updatePage}
                PaginationUtils={PaginationUtils}
              />
              <div className='col-md-3'>
                <div className='results-filter'>
                  <Col lg={12} md={12} sm={12} xs={12}></Col>
                </div>
              </div>
            </div>
            <div className='list-carrier table-scrollable table-container'>
              <table className='table-scrollable table-scroll  table table-striped table-hover '>
                <thead>
                  <tr role='row'>
                    {/* <th>
                      <Form.Check type='checkbox' />
                    </th> */}
                    <th className='fw-bold'>Avatar</th>
                    <th className='fw-bold'>Name</th>
                    <th className='fw-bold d-none d-lg-table-cell'>Contact information</th>
                    <th className='fw-bold d-none d-lg-table-cell'>Groups</th>
                    <th className='fw-bold d-none d-lg-table-cell'>Status</th>
                    <th className='fw-bold d-none d-lg-table-cell'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={8}>
                        <div className='d-flex flex-center' style={{ height: '30vh' }}>
                          <Spinner animation='border' />
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loading && dataTable.length === 0 && (
                    <tr>
                      <td colSpan={8}>
                        <div className='d-flex flex-center' style={{ height: '30vh' }}>
                          There are no quotes currently added
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    dataTable.map(({ node: user }) => (
                      <Fragment key={user.id}>
                        <tr role='row' className='odd parent'>
                          <td className='user-avatar'>
                            <UserAvatar
                              user={{
                                firstName: user.firstName,
                                lastName: user.lastName,
                                avatar: user.avatar,
                              }}
                              size={55}
                            />
                          </td>
                          <td>
                            {user.primaryGroup?.name === 'SuperAdministrador' && !isSuperAdmin ? (
                              <span className='text-muted'>
                                {user.firstName} {user.lastName}
                              </span>
                            ) : (
                              <Link className='text-info' to={`/user/${parseId(user.id)}`}>
                                {user.firstName} {user.lastName}
                              </Link>
                            )}
                            {user.primaryGroup && (
                              <>
                                <br />
                                <span className='fw-bold'>Role: </span>
                                {user.primaryGroup.name}
                              </>
                            )}
                            {user.npn && (
                              <>
                                <br />
                                <span className='fw-bold'>NPN: </span>
                                {user.npn}
                              </>
                            )}
                          </td>

                          <td className='d-none d-lg-table-cell'>
                            <a href={`mailto:${user.email}`}>{user.email}</a>
                            {user.cellphone && (
                              <>
                                <br />
                                <PhoneProviderActions phone={user.cellphone} />
                              </>
                            )}
                          </td>
                          <td className='d-none d-lg-table-cell'>
                            <div className='d-flex flex-col gap-2'>
                              {user.groupAssignmentSet?.edges?.length > 0 && (
                                <div>
                                  {user.groupAssignmentSet?.edges?.map(({ node: b }, idx) => (
                                    <div
                                      key={b.id}
                                      className={`d-flex align-items-center justify-content-between gap-1 py-2 px-2 bg-${getColorRamdomBoots(idx) || 'light'
                                        }`}
                                      style={{
                                        borderRadius: '3px',
                                        color: 'white',
                                        fontSize: '11px',
                                      }}
                                    >
                                      {b.name}
                                      <button
                                        style={{
                                          fontWeight: 'bolder',
                                          border: 0,
                                          backgroundColor: 'transparent',
                                          color: 'red',
                                        }}
                                      >
                                        X
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className='d-none d-lg-table-cell'>
                            <Badge
                              className='d-flex align-items-center justify-content-center'
                              bg={
                                user.isActive
                                  ? 'success'
                                  : user.isActive === false
                                    ? 'warning'
                                    : 'secondary'
                              }
                            >
                              {user.isActive
                                ? 'ACTIVE'
                                : user.isActive === false
                                  ? 'INACTIVE'
                                  : 'N/R'}
                            </Badge>
                          </td>
                          <td className='d-none d-lg-table-cell'>
                            {user.primaryGroup?.name === 'SuperAdministrador' && !isSuperAdmin ? null : (
                              <Dropdown as={ButtonGroup} drop='down'>
                                <Button
                                  variant='outline-primary'
                                  size='sm'
                                  style={{ border: '1px solid #3e97ff' }}
                                  onClick={(e) => navigate(`/user/${parseId(user.id)}`)}
                                >
                                  Preview
                                </Button>
                                <Dropdown.Toggle
                                  split
                                  variant='outline-primary'
                                  size='sm'
                                  id={`user-actions-${parseId(user.id)}`}
                                  style={{ border: '1px solid #3e97ff' }}
                                />
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() => handleResendInvite(user.email)}
                                  >
                                    Resend invitation
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}

                            {/* <Dropdown>
                              <Dropdown.Toggle size='sm' variant='success' id='dropdown-basic'>
                                Actions
                              </Dropdown.Toggle>

                              <Dropdown.Menu>
                                <Dropdown.Item as={Link} to={`/user/${parseId(user.id)}`}>
                                  View User
                                </Dropdown.Item>
                                
                              </Dropdown.Menu>
                            </Dropdown> */}
                          </td>
                        </tr>
                        {arrShowMoreTable.find((ar) => ar.id === user.id)?.show && (
                          <tr className='d-lg-none'>
                            <td colSpan={3}>
                              <ListGroup variant='flush' className='d-table mx-auto w-75'>
                                <ListGroup.Item>
                                  <div className='d-flex align-items-center justify-content-between'>
                                    <span className='fw-bold'>Contact information:</span>
                                    <div>
                                      <a href={`mailto:${user.email}`}>{user.email}</a>
                                      {user.cellphone && (
                                        <>
                                          <br />
                                          <PhoneProviderActions phone={user.cellphone} />
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                  <div className='d-flex align-items-center justify-content-between'>
                                    <span className='fw-bold'>Groups:</span>
                                    <div>
                                      <div className='d-flex flex-col gap-2'>
                                        {user.groupAssignmentSet?.edges?.length > 0 && (
                                          <div>
                                            {user.groupAssignmentSet?.edges?.map(({ node: b }, idx) => (
                                              <div
                                                key={b.id}
                                                className={`d-flex align-items-center justify-content-between gap-1 py-2 px-2 bg-${getColorRamdomBoots(idx) || 'light'
                                                  }`}
                                                style={{
                                                  borderRadius: '3px',
                                                  color: 'white',
                                                  fontSize: '11px',
                                                }}
                                              >
                                                {b.name}
                                                <button
                                                  style={{
                                                    fontWeight: 'bolder',
                                                    border: 0,
                                                    backgroundColor: 'transparent',
                                                    color: 'red',
                                                  }}
                                                >
                                                  X
                                                </button>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                  <div className='d-flex align-items-center justify-content-between'>
                                    <span className='fw-bold'>Status:</span>
                                    <div>
                                      <Badge
                                        className='d-flex'
                                        bg='success'
                                      /* bg={
                                      user.status === '0'
                                        ? 'danger'
                                        : user.status === '1'
                                        ? 'success'
                                        : 'dark'
                                    } */
                                      >
                                        ACTIVE
                                        {/* {user.status === '0'
                                        ? 'INACTIVE'
                                        : user.status === '1'
                                        ? 'ACTIVE'
                                        : 'N/R'} */}
                                      </Badge>
                                    </div>
                                  </div>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                  <div className='d-flex align-items-center justify-content-between'>
                                    <span className='fw-bold'>Actions:</span>
                                    <div>
                                      <Dropdown drop='down'>
                                        <Dropdown.Toggle
                                          size='sm'
                                          variant='success'
                                          id='dropdown-basic'
                                        >
                                          Actions
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                          <Dropdown.Item
                                            onClick={() => handleResendInvite(user.email)}
                                          >
                                            Resend invitation
                                          </Dropdown.Item>
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    </div>
                                  </div>
                                </ListGroup.Item>
                              </ListGroup>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && <ModalAddUser isOpen={isOpen} onClose={onClose} refetch={refetch} />}
      {isOpenManage && <ModalAddManage isOpen={isOpenManage} onClose={onCloseManage} />}
      {isOpenOffCanva && (
        <OffCanvaAsideFilters
          isOpen={isOpenOffCanva}
          onClose={onCloseOffCanva}
          setTabFilters={setTabFilters}
          tabFilters={formData.tabFilters}
          onOpenManage={onOpenManage}
        />
      )}
    </div>
  )
}
